import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  InputBase,
  Stack,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ViewStreamRoundedIcon from "@mui/icons-material/ViewStreamRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";

import Dialog from "../../../components/dialog/Dialog";
import Content from "../../../components/Content";
import { GetUserGroupsByOrganisationId } from "../../../hooks/useUserGroupdata";
import { CreateGroupModal } from "../../../modals/general/CreateGroupModal";
import { GroupEditModal } from "../customer/GroupEditView";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) => {
  const idx = hashString(name) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[idx];
};

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const FILTERS = [
  { id: "all", label: "Alla" },
  { id: "alarm", label: "Larm" },
  { id: "chat", label: "Chatt" },
  { id: "full", label: "Full åtkomst" },
];

const Avatar = ({ name, size = 48 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 10px 22px -12px ${a}55`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "14px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.22), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

const PermissionChip = ({ icon: Icon, label, active }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.75,
      height: 32,
      borderRadius: "999px",
      border: "1px solid",
      borderColor: active ? "rgba(43,140,77,0.32)" : "rgba(10,22,40,0.12)",
      background: active ? "rgba(43,140,77,0.1)" : "rgba(10,22,40,0.025)",
      color: active ? "#1a6536" : "rgba(95,104,119,0.75)",
      fontSize: "13px",
      fontWeight: 700,
      letterSpacing: "0.005em",
      fontFamily: '"DM Sans", sans-serif',
      transition: "all 0.25s ease",
      textDecoration: active ? "none" : "line-through",
      textDecorationColor: active ? "transparent" : "rgba(95,104,119,0.35)",
      textDecorationThickness: "1.5px",
    }}
  >
    <Box
      sx={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: active ? "#2b8c4d" : "#c7c3ba",
        boxShadow: active ? "0 0 0 4px rgba(43,140,77,0.18)" : "none",
        flexShrink: 0,
      }}
    />
    <Icon
      sx={{
        fontSize: 16,
        opacity: active ? 1 : 0.5,
      }}
    />
    {label}
  </Box>
);

const MetricCell = ({ label, value, total, tint, index }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box
      sx={{
        py: 3.5,
        pr: 3,
        pl: { xs: 0, md: index === 0 ? 0 : 3 },
        position: "relative",
        borderRight: {
          xs: "none",
          md: index < 3 ? `1px solid ${LINE}` : "none",
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${LINE}` : "none",
          md: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: INK_MUTED,
          mb: 1.5,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: "2.75rem",
            fontWeight: 500,
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Typography>
        {total > 0 && index > 0 && (
          <Typography
            sx={{
              fontSize: "12px",
              color: INK_MUTED,
              fontWeight: 500,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            / {total}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "2px",
          width: "100%",
          maxWidth: 180,
          background: LINE_SOFT,
          borderRadius: "2px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: tint,
            transformOrigin: "left",
            transform: `scaleX(${pct / 100})`,
          }}
        />
      </Box>
    </Box>
  );
};

const GroupRow = ({ group, onSelect, index }) => (
  <Box
    onClick={() => onSelect(group.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: "auto 1fr auto auto",
      alignItems: "center",
      gap: 3,
      px: 3,
      py: 2.5,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "14%",
        bottom: "14%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "& .row-name": {
          color: BRAND,
        },
      },
    }}
  >
    <Avatar name={group.group_name || "?"} size={46} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "17px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {group.group_name}
      </Typography>
    </Box>
    <Stack direction="row" spacing={1} sx={{ display: { xs: "none", sm: "flex" } }}>
      <PermissionChip
        icon={NotificationsActiveRoundedIcon}
        label="Larm"
        active={!!group.can_alarm}
      />
      <PermissionChip
        icon={ChatBubbleRoundedIcon}
        label="Chatt"
        active={!!group.can_chat}
      />
    </Stack>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const GroupCard = ({ group, onSelect, index }) => (
  <Box
    onClick={() => onSelect(group.id)}
    sx={{
      position: "relative",
      p: 3.5,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "48px",
        height: "2px",
        background: BRAND,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: "rgba(10,22,40,0.18)",
        transform: "translateY(-3px)",
        boxShadow:
          "0 24px 48px -24px rgba(10,22,40,0.16), 0 2px 6px -2px rgba(10,22,40,0.04)",
        "&::before": { transform: "scaleX(1)" },
        "& .card-arrow": { transform: "translateX(3px)", color: BRAND },
      },
    }}
  >
    <Box sx={{ mb: 3 }}>
      <Avatar name={group.group_name || "?"} size={56} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "22px",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
        mb: 0.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        minHeight: "53px",
      }}
    >
      {group.group_name}
    </Typography>
    <Typography
      sx={{
        fontSize: "12.5px",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
        mb: 3,
      }}
    >
      Användargrupp
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
      <PermissionChip
        icon={NotificationsActiveRoundedIcon}
        label="Larm"
        active={!!group.can_alarm}
      />
      <PermissionChip
        icon={ChatBubbleRoundedIcon}
        label="Chatt"
        active={!!group.can_chat}
      />
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 2.5,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: INK_MUTED,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        Redigera
      </Typography>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 18,
          color: INK_MUTED,
          transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

const EmptyState = ({ onCreate, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <GroupsRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga grupper ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 420,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm eller rensa filtren för att se alla grupper."
        : "Skapa din första grupp för att börja organisera användare och tilldela behörigheter."}
    </Typography>
    {!hasQuery && (
      <Box
        onClick={onCreate}
        component="button"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": {
            background: "#6b0d1d",
            transform: "translateY(-1px)",
          },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        Skapa din första grupp
      </Box>
    )}
  </Box>
);

export const GroupView = () => {
  const [organisationId, setOrganisationId] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  const { data: userGroupList, isLoading } =
    GetUserGroupsByOrganisationId(organisationId);

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  const groups = useMemo(
    () => (Array.isArray(userGroupList?.data) ? userGroupList.data : []),
    [userGroupList]
  );

  const metrics = useMemo(() => {
    let alarm = 0;
    let chat = 0;
    let full = 0;
    for (const g of groups) {
      if (g.can_alarm) alarm += 1;
      if (g.can_chat) chat += 1;
      if (g.can_alarm && g.can_chat) full += 1;
    }
    return { total: groups.length, alarm, chat, full };
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (q && !(g.group_name || "").toLowerCase().includes(q)) return false;
      if (filter === "alarm" && !g.can_alarm) return false;
      if (filter === "chat" && !g.can_chat) return false;
      if (filter === "full" && !(g.can_alarm && g.can_chat)) return false;
      return true;
    });
  }, [groups, search, filter]);

  const closeEditModal = () => setEditGroupId(null);

  const hasActiveQuery = !!search.trim() || filter !== "all";

  return (
    <Content>
      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 80,
            left: -140,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(16,50,74,0.07), rgba(16,50,74,0) 62%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            animation: `${floatOrb} 18s ease-in-out infinite reverse`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Organisation · Åtkomstkontroll
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Grupper
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 300,
                    fontStyle: "italic",
                    fontSize: "0.58em",
                    color: BRAND,
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  & behörigheter
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Organisera användare i logiska enheter och styr exakt vilka
                funktioner — larm, chatt eller full åtkomst — varje grupp har
                rätt att använda inom er organisation.
              </Typography>
            </Box>

            <Box
              component="button"
              onClick={() => setIsCreateModalOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2.75,
                py: 1.625,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.005em",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                boxShadow:
                  "0 14px 32px -14px rgba(10,22,40,0.45), 0 2px 4px -2px rgba(10,22,40,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${BRAND}, transparent 70%)`,
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 22px 44px -18px rgba(10,22,40,0.55), 0 2px 6px -2px rgba(10,22,40,0.12)",
                  "&::before": { opacity: 1 },
                },
                "& > *": { position: "relative", zIndex: 1 },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 18 }} />
              Ny grupp
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              borderTop: `1px solid ${LINE}`,
              borderBottom: `1px solid ${LINE}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
              backdropFilter: "blur(4px)",
            }}
          >
            <MetricCell
              label="Totalt antal grupper"
              value={metrics.total}
              total={metrics.total}
              tint={INK}
              index={0}
            />
            <MetricCell
              label="Med larmbehörighet"
              value={metrics.alarm}
              total={metrics.total}
              tint={BRAND}
              index={1}
            />
            <MetricCell
              label="Med chattbehörighet"
              value={metrics.chat}
              total={metrics.total}
              tint={NAVY}
              index={2}
            />
            <MetricCell
              label="Full åtkomst"
              value={metrics.full}
              total={metrics.total}
              tint="#2b8c4d"
              index={3}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              height: 44,
              minWidth: 280,
              flex: { xs: "1 1 100%", md: "0 1 340px" },
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              transition: "all 0.25s ease",
              "&:focus-within": {
                borderColor: "rgba(10,22,40,0.28)",
                boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök grupper..."
              sx={{
                flex: 1,
                fontSize: "14px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": {
                  color: INK_MUTED,
                  opacity: 1,
                },
              }}
            />
          </Box>

          <Stack direction="row" spacing={0.75}>
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <Box
                  key={f.id}
                  component="button"
                  onClick={() => setFilter(f.id)}
                  sx={{
                    px: 1.75,
                    height: 44,
                    background: active ? INK : "transparent",
                    color: active ? "#fff" : INK_MUTED,
                    border: `1px solid ${active ? INK : LINE}`,
                    borderRadius: "12px",
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: "12.5px",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      borderColor: active ? INK : "rgba(10,22,40,0.2)",
                      color: active ? "#fff" : INK,
                    },
                  }}
                >
                  {f.label}
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                px: 1.5,
                py: 0.75,
                color: INK_MUTED,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: INK,
                  color: "#fff",
                  "&:hover": { background: INK },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewStreamRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <ShieldRoundedIcon sx={{ fontSize: 14, color: INK_MUTED }} />
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filteredGroups.length} ${
                  filteredGroups.length === 1 ? "grupp" : "grupper"
                }${hasActiveQuery ? ` av ${groups.length}` : ""}`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom: i < 3 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton variant="rounded" width={46} height={46} sx={{ borderRadius: "14px" }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="38%" height={20} />
                    <Skeleton width="20%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                  <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: "999px" }} />
                  <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: "999px" }} />
                </Box>
              ))}
            </Box>
          ) : filteredGroups.length === 0 ? (
            <EmptyState
              onCreate={() => setIsCreateModalOpen(true)}
              hasQuery={hasActiveQuery}
            />
          ) : viewMode === "list" ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filteredGroups.map((group, i) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  onSelect={setEditGroupId}
                  index={i}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {filteredGroups.map((group, i) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  onSelect={setEditGroupId}
                  index={i}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        title="Lägg till grupp"
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <CreateGroupModal
          facilities={userGroupList?.data}
          customerid={organisationId}
          closeCreateModal={() => setIsCreateModalOpen(false)}
        />
      </Dialog>
      {editGroupId && (
        <Dialog
          title="Redigera grupp"
          open={!!editGroupId}
          onClose={closeEditModal}
        >
          <GroupEditModal groupId={editGroupId} onClose={closeEditModal} />
        </Dialog>
      )}
    </Content>
  );
};

export default GroupView;
